import {
    TooltipAlign,
    TooltipPosition,
    type TooltipPositionType,
    TooltipSize,
    type TooltipSizeType,
    TooltipVariant,
} from './types';

export const variantConfig = {
    [TooltipVariant.DARK]: {
        arrow: 'fill-midnight',
        content: 'bg-midnight text-white',
    },
    [TooltipVariant.LIGHT]: {
        arrow: 'fill-surface-light',
        content: 'bg-surface-light text-midnight shadow-[0px_2px_6px_0px_rgba(163,177,196,0.2)]',
    },
    [TooltipVariant.ERROR]: {
        arrow: 'fill-alert-error-bg',
        content: 'bg-alert-error-bg text-alert-error',
    },
    [TooltipVariant.WHITE]: {
        arrow: 'fill-white',
        content: 'bg-white text-midnight',
    },
} as const;

export const sizeConfig = {
    [TooltipSize.BIG]: {
        arrow: { height: 8, width: 16 },
        content: 'p-3',
    },
    [TooltipSize.SMALL]: {
        arrow: { height: 6, width: 12 },
        content: 'px-2.5 py-1.5',
    },
} as const;

export const positionConfig = {
    [TooltipPosition.BOTTOM]: {
        arrowOrder: 'order-first',
        arrowRotation: '',
        container: 'flex-col',
    },
    [TooltipPosition.LEFT]: {
        arrowOrder: 'order-last',
        arrowRotation: 'rotate-90',
        container: 'flex-row',
    },
    [TooltipPosition.RIGHT]: {
        arrowOrder: 'order-first',
        arrowRotation: '-rotate-90',
        container: 'flex-row',
    },
    [TooltipPosition.TOP]: {
        arrowOrder: 'order-last',
        arrowRotation: 'rotate-180',
        container: 'flex-col',
    },
} as const;

/**
 * Calculate the arrow margin based on position and size.
 * For LEFT/RIGHT positions, the rotated arrow needs a negative margin to compensate
 * for the empty space created by the rotation (layout box remains unchanged).
 * Empty space = (original_width - original_height) / 2
 */
export const getArrowMargin = (position: TooltipPositionType, size: TooltipSizeType): React.CSSProperties => {
    const { arrow } = sizeConfig[size];
    const rotationOffset = (arrow.width - arrow.height) / 2;

    switch (position) {
        case TooltipPosition.LEFT:
            return { marginLeft: -rotationOffset };
        case TooltipPosition.RIGHT:
            return { marginRight: -rotationOffset };
        case TooltipPosition.TOP:
            return { marginTop: -1 };
        case TooltipPosition.BOTTOM:
            return { marginBottom: -1 };
        default:
            return {};
    }
};

export const alignConfig = {
    [TooltipAlign.CENTER]: {
        horizontal: 'justify-center',
        vertical: 'items-center',
    },
    [TooltipAlign.END]: {
        horizontal: 'justify-end pr-3',
        vertical: 'items-end pb-3',
    },
    [TooltipAlign.START]: {
        horizontal: 'justify-start pl-3',
        vertical: 'items-start pt-3',
    },
} as const;
